import * as React from 'react'
import dynamic from 'next/dynamic'

import { i18n } from '@thg-commerce/enterprise-core'
import { Button } from '@thg-commerce/gravity-elements'

const SvgCarbs = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Carbs'),
)
const SvgProtein = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Protein'),
)
const SvgFats = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Fats'),
)

import {
  MacroResultsContainer,
  MacroResultsTitle,
  MacroResultsGrid,
  MacroResultsGridItem,
  MacroResultsLabel,
  MacroResultsValue,
  MacroResultsButtonsContainer,
  MacroResultsCalculateAgainLink,
} from './styles'

export interface MacroResultsProps {
  macros: {
    carbohydrate: number
    protein: number
    fat: number
  }
  buttonLink: string
  setShowResultsView: (value: boolean) => void
}

export const MacroResults = (props: MacroResultsProps) => {
  const i18nText = {
    title: i18n('widget.macrocalculator.results.title'),
    carbs: i18n('widget.macrocalculator.results.carbs'),
    protein: i18n('widget.macrocalculator.results.protein'),
    fats: i18n('widget.macrocalculator.results.fats'),
    goalsButton: i18n('widget.macrocalculator.results.goalsbutton'),
    calculateAgain: i18n('widget.macrocalculator.results.calculateagain'),
  }

  const ref = React.createRef<HTMLDivElement>()

  React.useEffect(() => {
    if (ref !== null && ref.current !== null) {
      window.scrollTo(0, ref.current.offsetTop - 150)
    }
  }, [])

  const carbohydratesIntake = Math.max(0, props.macros.carbohydrate)
  const proteinIntake = Math.max(0, props.macros.protein)
  const fatIntake = Math.max(0, props.macros.fat)

  return (
    <MacroResultsContainer data-testid="macro-results-container" ref={ref}>
      <MacroResultsTitle>
        {i18nText.title || 'Your daily intake'}
      </MacroResultsTitle>
      <MacroResultsGrid>
        <MacroResultsGridItem colSpan={[4, 4, 2, 2]} colStart={[1, 1, 4, 4]}>
          <SvgCarbs />
          <MacroResultsLabel>{i18nText.carbs || 'Carbs'}</MacroResultsLabel>
          <MacroResultsValue>{`${carbohydratesIntake.toFixed(
            0,
          )}g`}</MacroResultsValue>
        </MacroResultsGridItem>

        <MacroResultsGridItem colSpan={[4, 4, 2, 2]} colStart={[5, 5, 6, 6]}>
          <SvgProtein />
          <MacroResultsLabel>{i18nText.protein || 'Protein'}</MacroResultsLabel>
          <MacroResultsValue>{`${proteinIntake.toFixed(
            0,
          )}g`}</MacroResultsValue>
        </MacroResultsGridItem>

        <MacroResultsGridItem colSpan={[4, 4, 2, 2]} colStart={[9, 9, 8, 8]}>
          <SvgFats />
          <MacroResultsLabel>{i18nText.fats || 'Fats'}</MacroResultsLabel>
          <MacroResultsValue>{`${fatIntake.toFixed(0)}g`}</MacroResultsValue>
        </MacroResultsGridItem>
      </MacroResultsGrid>

      <MacroResultsButtonsContainer>
        <Button
          href={props.buttonLink}
          sizing="regular"
          emphasis="high"
          renderedAs="a"
        >
          {i18nText.goalsButton || 'Shop for your goals'}
        </Button>
        <MacroResultsCalculateAgainLink
          sizing="regular"
          emphasis="low"
          renderedAs="a"
          href=""
          onClick={() => {
            props.setShowResultsView(false)
          }}
          data-testid="calculate-again"
        >
          {i18nText.calculateAgain || 'Calculate again'}
        </MacroResultsCalculateAgainLink>
      </MacroResultsButtonsContainer>
    </MacroResultsContainer>
  )
}
